import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ChevronLeft, Receipt, Plus, ShoppingCart, CreditCard, Coins, Wallet, Package,
    Edit3, X, RefreshCw, Link
} from 'lucide-react';
import { clubsService } from '../../../services';
import './ManageClubs.css';
import VenueSubnav from './VenueSubnav';
import VenueAccessBanner from './VenueAccessBanner';

const emptyProductForm = {
    name: '',
    sku: '',
    category: '',
    barcode: '',
    unitPrice: '',
    vatRatePercent: '20',
    isActive: true,
    trackInventory: false,
    stockOnHand: ''
};

const createCheckoutRequestId = () => {
    if (typeof crypto !== 'undefined' && crypto?.randomUUID) {
        return crypto.randomUUID();
    }

    return `checkout-${Date.now()}-${Math.floor(Math.random() * 100000)}`;
};

const ClubTill = () => {
    const { clubId } = useParams();
    const navigate = useNavigate();
    const [club, setClub] = useState(null);
    const [products, setProducts] = useState([]);
    const [sales, setSales] = useState([]);
    const [cart, setCart] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [includeInactive, setIncludeInactive] = useState(false);
    const [showProductModal, setShowProductModal] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [productForm, setProductForm] = useState(emptyProductForm);
    const [isSavingProduct, setIsSavingProduct] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState('Card');
    const [paymentProvider, setPaymentProvider] = useState('Manual');
    const [paymentReference, setPaymentReference] = useState('');
    const [cardIntentStatus, setCardIntentStatus] = useState(null);
    const [receivedAmount, setReceivedAmount] = useState('');
    const [isCheckingOut, setIsCheckingOut] = useState(false);
    const [checkoutMessage, setCheckoutMessage] = useState(null);
    const [checkoutRequestId, setCheckoutRequestId] = useState(createCheckoutRequestId());
    const [selectedSale, setSelectedSale] = useState(null);
    const [isLoadingSale, setIsLoadingSale] = useState(false);

    const load = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const [clubData, productData, salesData] = await Promise.all([
                clubsService.getClub(clubId),
                clubsService.getClubTillProducts(clubId, includeInactive),
                clubsService.getClubTillSales(clubId, 40)
            ]);
            setClub(clubData);
            setProducts(Array.isArray(productData) ? productData : []);
            setSales(Array.isArray(salesData) ? salesData : []);
        } catch (err) {
            setError(err.message || 'Failed to load till data.');
        } finally {
            setIsLoading(false);
        }
    }, [clubId, includeInactive]);

    useEffect(() => {
        load();
    }, [load]);

    const isPremiumVenue = Boolean(club?.isPremiumVenue || (club?.profileTier || '').toLowerCase() === 'premium');
    const openUpgrade = () => navigate(`/dashboard/club/${clubId}/plan`);

    const cartTotals = useMemo(() => {
        const subtotal = cart.reduce((sum, item) => sum + (item.unitPrice * item.quantity), 0);
        const taxTotal = cart.reduce((sum, item) => sum + ((item.unitPrice * item.quantity) * ((item.vatRatePercent || 0) / 100)), 0);
        const total = subtotal + taxTotal;
        return {
            subtotal: Number(subtotal.toFixed(2)),
            taxTotal: Number(taxTotal.toFixed(2)),
            total: Number(total.toFixed(2))
        };
    }, [cart]);

    const openCreateProduct = () => {
        if (!isPremiumVenue) {
            openUpgrade();
            return;
        }

        setEditingProduct(null);
        setProductForm(emptyProductForm);
        setShowProductModal(true);
    };

    const openEditProduct = (product) => {
        if (!isPremiumVenue) {
            openUpgrade();
            return;
        }

        setEditingProduct(product);
        setProductForm({
            name: product.name || '',
            sku: product.sku || '',
            category: product.category || '',
            barcode: product.barcode || '',
            unitPrice: String(product.unitPrice ?? ''),
            vatRatePercent: String(product.vatRatePercent ?? 20),
            isActive: Boolean(product.isActive),
            trackInventory: Boolean(product.trackInventory),
            stockOnHand: product.stockOnHand === null || product.stockOnHand === undefined ? '' : String(product.stockOnHand)
        });
        setShowProductModal(true);
    };

    const saveProduct = async () => {
        if (!isPremiumVenue) {
            openUpgrade();
            return;
        }

        if (!productForm.name.trim()) {
            return;
        }

        setIsSavingProduct(true);
        setError(null);
        try {
            const payload = {
                name: productForm.name.trim(),
                sku: productForm.sku.trim() || null,
                category: productForm.category.trim() || null,
                barcode: productForm.barcode.trim() || null,
                unitPrice: Number(productForm.unitPrice || 0),
                vatRatePercent: Number(productForm.vatRatePercent || 0),
                isActive: productForm.isActive,
                trackInventory: productForm.trackInventory,
                stockOnHand: productForm.trackInventory && productForm.stockOnHand !== '' ? Number(productForm.stockOnHand) : null
            };

            if (editingProduct) {
                await clubsService.updateClubTillProduct(clubId, editingProduct.clubTillProductId, payload);
            } else {
                await clubsService.createClubTillProduct(clubId, payload);
            }

            setShowProductModal(false);
            await load();
        } catch (err) {
            setError(err.message || 'Failed to save product.');
        } finally {
            setIsSavingProduct(false);
        }
    };

    const addProductToCart = (product) => {
        if (!isPremiumVenue) {
            openUpgrade();
            return;
        }

        setCheckoutMessage(null);
        setCart((prev) => {
            const existing = prev.find((item) => item.productId === product.clubTillProductId);
            if (existing) {
                return prev.map((item) => item.productId === product.clubTillProductId
                    ? { ...item, quantity: Number((item.quantity + 1).toFixed(3)) }
                    : item);
            }

            return [
                ...prev,
                {
                    key: `prod-${product.clubTillProductId}`,
                    productId: product.clubTillProductId,
                    name: product.name,
                    unitPrice: Number(product.unitPrice || 0),
                    vatRatePercent: Number(product.vatRatePercent || 0),
                    quantity: 1
                }
            ];
        });
    };

    const addCustomLine = () => {
        if (!isPremiumVenue) {
            openUpgrade();
            return;
        }

        setCheckoutMessage(null);
        setCart((prev) => [
            ...prev,
            {
                key: `custom-${Date.now()}-${prev.length}`,
                productId: null,
                name: 'Custom line',
                unitPrice: 0,
                vatRatePercent: 20,
                quantity: 1
            }
        ]);
    };

    const updateCartLine = (key, patch) => {
        if (!isPremiumVenue) {
            openUpgrade();
            return;
        }

        setCheckoutMessage(null);
        setCart((prev) => prev.map((line) => (line.key === key ? { ...line, ...patch } : line)));
    };

    const removeCartLine = (key) => {
        if (!isPremiumVenue) {
            openUpgrade();
            return;
        }

        setCheckoutMessage(null);
        setCart((prev) => prev.filter((line) => line.key !== key));
    };

    const submitCheckout = async () => {
        if (!isPremiumVenue) {
            openUpgrade();
            return;
        }

        if (!cart.length) {
            return;
        }

        setIsCheckingOut(true);
        setError(null);
        setCheckoutMessage(null);

        try {
            const payload = {
                checkoutRequestId,
                paymentMethod,
                paymentProvider,
                paymentReference: paymentReference || null,
                receivedAmount: receivedAmount !== '' ? Number(receivedAmount) : null,
                items: cart.map((item) => ({
                    clubTillProductId: item.productId,
                    name: item.productId ? null : item.name,
                    quantity: Number(item.quantity),
                    unitPrice: item.productId ? null : Number(item.unitPrice),
                    vatRatePercent: item.productId ? null : Number(item.vatRatePercent)
                }))
            };

            const result = await clubsService.checkoutClubTill(clubId, payload);
            const sale = result?.sale;

            setCheckoutMessage(result?.replayed
                ? `Previous checkout recovered: ${sale?.saleReference || 'sale found'}`
                : `Sale completed: ${sale?.saleReference || 'success'}`);

            setCart([]);
            setReceivedAmount('');
            setPaymentProvider('Manual');
            setPaymentReference('');
            setCardIntentStatus(null);
            setCheckoutRequestId(createCheckoutRequestId());
            await load();
        } catch (err) {
            setError(err.message || 'Checkout failed. You can retry with the same request id.');
        } finally {
            setIsCheckingOut(false);
        }
    };

    const createCardIntent = async () => {
        if (!isPremiumVenue) {
            openUpgrade();
            return;
        }

        if (cartTotals.total <= 0) {
            return;
        }

        setError(null);
        try {
            const result = await clubsService.createClubTillCardIntent(clubId, {
                amount: cartTotals.total,
                currency: 'gbp',
                description: `${club?.name || 'Club'} till checkout`
            });
            setPaymentMethod('Card');
            setPaymentProvider(result?.provider || 'Stripe');
            setPaymentReference(result?.paymentIntentId || '');
            setCardIntentStatus(result?.status || 'created');
            setCheckoutMessage(`Card intent created: ${result?.paymentIntentId}`);
        } catch (err) {
            setError(err.message || 'Failed to create card payment intent.');
        }
    };

    const refreshCardIntentStatus = async () => {
        if (!isPremiumVenue) {
            openUpgrade();
            return;
        }

        if (!paymentReference) {
            return;
        }

        setError(null);
        try {
            const status = await clubsService.getClubTillCardIntentStatus(clubId, paymentReference);
            setCardIntentStatus(status?.status || null);
            setCheckoutMessage(`Card status: ${status?.status || 'unknown'}`);
        } catch (err) {
            setError(err.message || 'Failed to refresh card payment status.');
        }
    };

    const openSale = async (saleId) => {
        setIsLoadingSale(true);
        setError(null);
        try {
            const sale = await clubsService.getClubTillSale(clubId, saleId);
            setSelectedSale(sale);
        } catch (err) {
            setError(err.message || 'Failed to load sale details.');
        } finally {
            setIsLoadingSale(false);
        }
    };

    if (isLoading) {
        return (
            <div className="manage-clubs-page">
                <div className="manage-clubs-loading">
                    <div className="manage-clubs-spinner"></div>
                    <p>Loading till system...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="manage-clubs-page">
            <button className="club-dashboard-back" onClick={() => navigate(`/dashboard/club/${clubId}/dashboard`)}>
                <ChevronLeft size={18} />
                Back to {club?.name || 'Venue'} Venue Dashboard
            </button>

            <VenueSubnav clubId={clubId} />

            <VenueAccessBanner club={club} clubId={clubId} navigate={navigate} premiumOnlyFeature featureLabel="Till / POS" />

            <div className="manage-section-header">
                <div className="manage-section-header-content">
                    <div className="manage-section-header-icon icon-green">
                        <Receipt size={24} />
                    </div>
                    <div>
                        <h1>Club Till</h1>
                        <p>Take payments at the counter, manage till products, and track sales.</p>
                    </div>
                </div>
                <button className="manage-section-add-btn" onClick={openCreateProduct} disabled={!isPremiumVenue}>
                    <Plus size={18} />
                    {isPremiumVenue ? 'Add Till Product' : 'Upgrade For Till / POS'}
                </button>
            </div>

            {!isPremiumVenue ? (
                <div className="venue-premium-lock-note">
                    Till / POS is a Premium venue feature. Free venues can inspect the screen, but product setup and checkout stay locked until the venue is upgraded.
                </div>
            ) : null}

            {error && <div className="manage-clubs-error">{error}</div>}
            {checkoutMessage && <div className="club-till-success">{checkoutMessage}</div>}

            <div className="club-till-grid">
                <section className="club-till-panel">
                    <div className="club-till-panel-header">
                        <h3>
                            <Package size={16} />
                            Product Catalogue
                        </h3>
                        <label className="club-till-inline-toggle">
                            <input
                                type="checkbox"
                                checked={includeInactive}
                                onChange={(e) => setIncludeInactive(e.target.checked)}
                                disabled={!isPremiumVenue}
                            />
                            Include inactive
                        </label>
                    </div>
                    <div className="club-till-product-list">
                        {products.length === 0 ? (
                            <p className="club-till-muted">No till products yet.</p>
                        ) : products.map((product) => (
                            <div key={product.clubTillProductId} className="club-till-product-row">
                                <div>
                                    <strong>{product.name}</strong>
                                    <div className="club-till-product-meta">
                                        {(product.category || 'General')} | {product.sku || 'No SKU'} | GBP {Number(product.unitPrice || 0).toFixed(2)}
                                    </div>
                                </div>
                                <div className="club-till-product-actions">
                                    <button className="btn-secondary" onClick={() => openEditProduct(product)} disabled={!isPremiumVenue}>
                                        <Edit3 size={15} />
                                    </button>
                                    <button className="btn-primary" onClick={() => addProductToCart(product)} disabled={!isPremiumVenue}>
                                        <Plus size={15} />
                                        Add
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                <section className="club-till-panel">
                    <div className="club-till-panel-header">
                        <h3>
                            <ShoppingCart size={16} />
                            Basket
                        </h3>
                        <button className="btn-secondary" onClick={addCustomLine} disabled={!isPremiumVenue}>Add custom line</button>
                    </div>
                    <div className="club-till-cart-list">
                        {cart.length === 0 ? (
                            <p className="club-till-muted">Basket is empty.</p>
                        ) : cart.map((line) => (
                            <div key={line.key} className="club-till-cart-row">
                                <input
                                    value={line.name}
                                    onChange={(e) => updateCartLine(line.key, { name: e.target.value })}
                                    disabled={Boolean(line.productId) || !isPremiumVenue}
                                />
                                <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={line.unitPrice}
                                    onChange={(e) => updateCartLine(line.key, { unitPrice: Number(e.target.value || 0) })}
                                    disabled={Boolean(line.productId) || !isPremiumVenue}
                                />
                                <input
                                    type="number"
                                    min="0.001"
                                    step="0.001"
                                    value={line.quantity}
                                    onChange={(e) => updateCartLine(line.key, { quantity: Number(e.target.value || 0) })}
                                    disabled={!isPremiumVenue}
                                />
                                <button className="btn-secondary" onClick={() => removeCartLine(line.key)} disabled={!isPremiumVenue}>
                                    <X size={14} />
                                </button>
                            </div>
                        ))}
                    </div>

                    <div className="club-till-totals">
                        <div><span>Subtotal</span><strong>GBP {cartTotals.subtotal.toFixed(2)}</strong></div>
                        <div><span>VAT</span><strong>GBP {cartTotals.taxTotal.toFixed(2)}</strong></div>
                        <div className="club-till-total-row"><span>Total</span><strong>GBP {cartTotals.total.toFixed(2)}</strong></div>
                    </div>

                    <div className="club-till-checkout">
                        <label>
                            Payment Method
                            <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} disabled={!isPremiumVenue}>
                                <option value="Card">Card</option>
                                <option value="Cash">Cash</option>
                                <option value="BankTransfer">Bank Transfer</option>
                                <option value="Other">Other</option>
                            </select>
                        </label>
                        <label>
                            Payment Provider
                            <input
                                value={paymentProvider}
                                onChange={(e) => setPaymentProvider(e.target.value)}
                                placeholder="Manual or Stripe"
                                disabled={!isPremiumVenue}
                            />
                        </label>
                        <label>
                            Received Amount (optional)
                            <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={receivedAmount}
                                onChange={(e) => setReceivedAmount(e.target.value)}
                                placeholder={cartTotals.total.toFixed(2)}
                                disabled={!isPremiumVenue}
                            />
                        </label>
                        <label>
                            Provider Reference (optional)
                            <input
                                value={paymentReference}
                                onChange={(e) => setPaymentReference(e.target.value)}
                                placeholder="Payment intent / terminal reference"
                                disabled={!isPremiumVenue}
                            />
                        </label>
                        {paymentMethod === 'Card' && (
                            <div className="club-till-card-actions">
                                <button className="btn-secondary" onClick={createCardIntent} disabled={cartTotals.total <= 0 || !isPremiumVenue}>
                                    <Link size={14} />
                                    Create Stripe Card Intent
                                </button>
                                <button className="btn-secondary" onClick={refreshCardIntentStatus} disabled={!paymentReference || !isPremiumVenue}>
                                    <RefreshCw size={14} />
                                    Refresh Card Status
                                </button>
                                {cardIntentStatus ? <span>Current: {cardIntentStatus}</span> : null}
                            </div>
                        )}
                        <div className="club-till-checkout-meta">
                            <RefreshCw size={14} />
                            Request ID: {checkoutRequestId}
                        </div>
                        <button className="btn-primary" onClick={submitCheckout} disabled={!cart.length || isCheckingOut || !isPremiumVenue}>
                            {paymentMethod === 'Cash' ? <Coins size={16} /> : paymentMethod === 'Card' ? <CreditCard size={16} /> : <Wallet size={16} />}
                            {isCheckingOut ? 'Processing...' : 'Complete Sale'}
                        </button>
                    </div>
                </section>
            </div>

            <section className="club-till-panel club-till-sales-panel">
                <div className="club-till-panel-header">
                    <h3>Recent Sales</h3>
                </div>
                <div className="club-till-sales-list">
                    {sales.length === 0 ? (
                        <p className="club-till-muted">No sales yet.</p>
                    ) : sales.map((sale) => (
                        <button
                            type="button"
                            key={sale.saleId}
                            className="club-till-sale-row"
                            onClick={() => openSale(sale.saleId)}
                        >
                            <div>
                                <strong>{sale.saleReference}</strong>
                                <span>{new Date(sale.createdAt).toLocaleString()}</span>
                            </div>
                            <strong>GBP {Number(sale.total || 0).toFixed(2)}</strong>
                        </button>
                    ))}
                </div>
            </section>

            {showProductModal && (
                <div className="modal-overlay" onClick={() => setShowProductModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{editingProduct ? 'Edit Till Product' : 'Add Till Product'}</h2>
                            <button className="modal-close" onClick={() => setShowProductModal(false)}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <label>Name</label>
                                <input value={productForm.name} onChange={(e) => setProductForm({ ...productForm, name: e.target.value })} />
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>SKU</label>
                                    <input value={productForm.sku} onChange={(e) => setProductForm({ ...productForm, sku: e.target.value })} />
                                </div>
                                <div className="form-group">
                                    <label>Category</label>
                                    <input value={productForm.category} onChange={(e) => setProductForm({ ...productForm, category: e.target.value })} />
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Unit Price</label>
                                    <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={productForm.unitPrice}
                                        onChange={(e) => setProductForm({ ...productForm, unitPrice: e.target.value })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>VAT %</label>
                                    <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={productForm.vatRatePercent}
                                        onChange={(e) => setProductForm({ ...productForm, vatRatePercent: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Barcode</label>
                                <input value={productForm.barcode} onChange={(e) => setProductForm({ ...productForm, barcode: e.target.value })} />
                            </div>
                            <div className="form-group">
                                <label style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <input
                                        type="checkbox"
                                        checked={productForm.trackInventory}
                                        onChange={(e) => setProductForm({ ...productForm, trackInventory: e.target.checked })}
                                    />
                                    Track inventory
                                </label>
                            </div>
                            {productForm.trackInventory && (
                                <div className="form-group">
                                    <label>Stock on hand</label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={productForm.stockOnHand}
                                        onChange={(e) => setProductForm({ ...productForm, stockOnHand: e.target.value })}
                                    />
                                </div>
                            )}
                            <div className="form-group">
                                <label style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <input
                                        type="checkbox"
                                        checked={productForm.isActive}
                                        onChange={(e) => setProductForm({ ...productForm, isActive: e.target.checked })}
                                    />
                                    Product active
                                </label>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn-secondary" onClick={() => setShowProductModal(false)}>Cancel</button>
                            <button className="btn-primary" onClick={saveProduct} disabled={isSavingProduct || !productForm.name.trim()}>
                                {isSavingProduct ? 'Saving...' : editingProduct ? 'Update Product' : 'Create Product'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {selectedSale && (
                <div className="modal-overlay" onClick={() => setSelectedSale(null)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Sale {selectedSale.saleReference}</h2>
                            <button className="modal-close" onClick={() => setSelectedSale(null)}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className="modal-body">
                            {isLoadingSale ? <p>Loading sale...</p> : (
                                <>
                                    <p><strong>Status:</strong> {selectedSale.status}</p>
                                    <p><strong>Total:</strong> GBP {Number(selectedSale.total || 0).toFixed(2)}</p>
                                    <p><strong>Created:</strong> {new Date(selectedSale.createdAt).toLocaleString()}</p>
                                    <div className="club-till-sale-detail-lines">
                                        {(selectedSale.lines || []).map((line) => (
                                            <div key={line.lineId}>
                                                <strong>{line.name}</strong>
                                                <span>{line.quantity} x GBP {Number(line.unitPrice || 0).toFixed(2)} = GBP {Number(line.lineTotal || 0).toFixed(2)}</span>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button className="btn-secondary" onClick={() => setSelectedSale(null)}>Close</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClubTill;
